import { type FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import { useDeviceInfo } from '@xing-com/crate-xinglet';

import { useStickyLinkbar } from '../../hooks/use-sticky-linkbar';
import { EXTERNAL_LINKS } from '../../routes';
import { trackingParams, useNwtTrackingCompanies } from '../../utils/tracking';
import type { BaseContentSwitcherDataProps } from '../base-content-switcher/base-content-switcher';

import * as Styled from './link-bar.styles';

const resolveStylesLinkbar = (
  stickBarFixed: boolean,
  isWebview: boolean
): string => {
  if (stickBarFixed) {
    return isWebview ? 'sticky stickyWebview' : 'sticky';
  }

  return 'inner';
};

export interface LinkBarNavigationProps extends BaseContentSwitcherDataProps {
  hidden?: boolean;
}

export type LinkBarProps = {
  navigation?: Array<LinkBarNavigationProps>;
};
const LinkBar: FC<LinkBarProps> = ({ navigation = [] }) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const { isWebview, mobileProtocol } = useDeviceInfo();
  const { ref, stickBarFixed } = useStickyLinkbar();
  const trackNwtEvent = useNwtTrackingCompanies();

  const notHiddenNavigation = navigation.filter(({ hidden }) => !hidden);

  const activeIndex = useMemo(
    () => notHiddenNavigation.findIndex(({ to }) => to?.includes(pathname)),
    [pathname]
  );

  const idealEmployerLink = isWebview
    ? `${mobileProtocol}${EXTERNAL_LINKS.preferences.mobileNative}`
    : EXTERNAL_LINKS.preferences.web;

  return (
    <Styled.Container ref={ref}>
      <div className={resolveStylesLinkbar(stickBarFixed, isWebview)}>
        <Styled.ContentSwitcher
          activeItemIndex={activeIndex}
          data={notHiddenNavigation}
          size="small"
          variant="filled"
        />
        <Styled.ProjobsButtonWrapper>
          <Styled.ProjobsButton
            size="small"
            variant="secondary"
            href={idealEmployerLink}
            // @ts-expect-error FIXME: SC6
            target="_self"
            trackingAttributes={trackingParams.projobs}
            data-testid="projobs-button"
            onClick={() => {
              trackNwtEvent({
                eventSchema: 'basic',
                event: 'opened',
                element: 'ideal_employers',
                page: 'companies/overview',
              });
            }}
          >
            {formatMessage({ id: 'CP_IDEAL_EMPLOYER_LIST' })}
          </Styled.ProjobsButton>
        </Styled.ProjobsButtonWrapper>
      </div>
    </Styled.Container>
  );
};

export default LinkBar;
